import styles from "./PublicBookingScreen.module.scss";

import type { ResolvePublicBookingResponse } from "@/modules/coaching/public-booking/services/resolvePublicBookingLink";

import { useCallback } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { AvailabilityErrorPanel } from "@/modules/coaching/booking/components/AvailabilityErrorPanel";
import { BookingWidget } from "@/modules/coaching/booking/components/BookingWidget";
import { BookingContextProvider } from "@/modules/coaching/booking/context/BookingContext";
import { AppLoadingIndicator } from "@/modules/shared/components/AppLoadingIndicator/AppLoadingIndicator";
import { PageContainer } from "@/modules/shared/components/PageContainer";
import { ROUTES } from "@/modules/shared/consts/routes";
import { isClientError } from "@/modules/shared/utils/isClientError";

import { publicBookingLinkQuery } from "../../queries/publicBookingLinkQuery";

interface PublicBookingScreenProps {
  coachSlug: string;
  bookingCode: string;
  placeholderData?: ResolvePublicBookingResponse;
  memberName?: string;
  memberEmail?: string;
}

export function PublicBookingScreen({
  coachSlug,
  bookingCode,
  placeholderData,
  memberName,
  memberEmail,
}: PublicBookingScreenProps) {
  const { t } = useTranslation();

  const { data, error, errorUpdateCount, isLoading, isFetching, refetch } =
    useQuery(publicBookingLinkQuery(coachSlug, bookingCode, placeholderData));

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading && !data) {
    return (
      <PageContainer variant="centered">
        <div
          className={styles.loadingContainer}
          data-testid="public-booking-loading"
        >
          <AppLoadingIndicator size="large" />
        </div>
      </PageContainer>
    );
  }

  // Bifrost answers 404 for an unknown slug or code and 400 for a malformed one.
  if (isClientError(error)) {
    return (
      <PageContainer variant="centered">
        <div className={styles.errorContainer}>
          <p className={styles.title}>{t("publicBooking.notFound.title")}</p>
          <p className={styles.description}>
            {t("publicBooking.notFound.description")}
          </p>
        </div>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer variant="centered">
        <AvailabilityErrorPanel
          isRepeatFailure={errorUpdateCount > 1}
          isRetrying={isFetching}
          onRetry={handleRetry}
          supportRoute={ROUTES.support.index}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer variant="centered">
      <div className={styles.widgetWrapper}>
        <BookingContextProvider
          initialData={{
            coachId: data.coachId,
            coachName: data.coachName,
            memberName,
            memberEmail,
            isPrefillEditable: Boolean(memberName || memberEmail),
          }}
        >
          <BookingWidget compact supportRoute={ROUTES.support.index} />
        </BookingContextProvider>
      </div>
    </PageContainer>
  );
}
